import { toasterService, ToastColor } from "components/feedback/Toaster";
import { DisplayTypeEnum } from "api/files";
import { dispatchUnauthorized } from "core/constants/AuthEvents";
import { getAccessToken } from "core/auth/accessTokenStorage";

interface NotificationResponse {
  title?: string;
  subtitle?: string;
  message: string;
  displayType: DisplayTypeEnum;
  color?: ToastColor;
}

interface ApiResponseWithNotification {
  notification?: NotificationResponse;
  [key: string]: unknown;
}

const handleNotification = (data: ApiResponseWithNotification) => {
  if (data?.notification && data.notification.displayType === DisplayTypeEnum.Toaster) {
    toasterService.showToast({
      title: data.notification.title,
      subtitle: data.notification.subtitle,
      message: data.notification.message,
      displayType: data.notification.displayType,
      color: data.notification.color
    });
  }
};

const wrapResponseWithNotificationInterceptor = (response: Response): Response => {
  const originalJson = response.json.bind(response);

  const wrappedJson = async () => {
    const data = await originalJson();
    handleNotification(data);
    return data;
  };

  return new Proxy(response, {
    get(target, prop) {
      if (prop === 'json') {
        return wrappedJson;
      }
      const value = target[prop as keyof Response];
      if (typeof value === 'function') {
        return value.bind(target);
      }
      return value;
    }
  });
};

export function createAuthInterceptor() {
  return async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const token = getAccessToken();

    const headers = new Headers(init?.headers);
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    const response = await fetch(input, { ...init, headers });

    if (response.status === 401) {
      dispatchUnauthorized();
      return response;
    }

    return wrapResponseWithNotificationInterceptor(response);
  };
}
